<template>
  <OnBoardingFrame
    as-form
    back
    :step="6"
    :title="$t('on_boarding.profile_settings_page.title')"
    :subtitle="$t('on_boarding.profile_settings_page.subtitle')"
    :error="errorMessage"
    @back="backTapped"
    @submit="submit"
  >
    <!-- Step 6. Mirrors the mobile settings step (iOS's
         OnBoardingAppSettingsViewController, Android's OnboardingSettingsFragment):
         units, coordinates, hiking pace and language.

         Mobile also offers a temperature toggle. Web deliberately doesn't: there
         is no temperature column on users and no GraphQL argument for it, because
         both apps keep that preference on the device. A web control that silently
         forgot would be worse than its absence.

         Every field is optional — each has a server-side default — so Continue is
         always available, and a list Rails didn't publish simply isn't shown.

         Each field carries a one-line hint saying what the setting changes,
         the same copy as the apps' settings step. The step fits a laptop
         window without scrolling: see .ob-fields--compact in onboarding.css. -->
    <div
      v-if="options.measurement.length || options.gridReference.length || hikeTimePresets.length || locales.length > 1"
      class="ob-fields ob-fields--compact"
    >
      <div v-if="options.measurement.length" class="ob-field">
        <label class="ob-label" for="settingsMeasurement">
          {{ $t('on_boarding.profile_settings_page.measurement') }}
        </label>
        <p id="settingsMeasurementHint" class="ob-hint">{{ $t('on_boarding.profile_settings_page.measurement_hint') }}</p>
        <select id="settingsMeasurement" aria-describedby="settingsMeasurementHint" v-model="measurement" class="ob-input">
          <option :value="null">{{ $t('on_boarding.profile_settings_page.not_set') }}</option>
          <option v-for="option in options.measurement" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-if="options.gridReference.length" class="ob-field">
        <div class="ob-label-row">
          <label class="ob-label" for="settingsGridReference">
            {{ $t('on_boarding.profile_settings_page.grid_reference') }}
          </label>
          <a
            class="ob-info settings-info-link"
            data-info="gridReference"
            :href="infoUrls.gridReference"
            target="_blank"
            rel="noopener"
            :aria-label="$t('on_boarding.profile_settings_page.grid_reference_info')"
            :title="$t('on_boarding.profile_settings_page.grid_reference_info')"
          ><span class="ms" aria-hidden="true">info</span></a>
        </div>
        <p id="settingsGridReferenceHint" class="ob-hint">{{ $t('on_boarding.profile_settings_page.grid_reference_hint') }}</p>
        <select id="settingsGridReference" aria-describedby="settingsGridReferenceHint" v-model="gridReference" class="ob-input">
          <option :value="null">{{ $t('on_boarding.profile_settings_page.not_set') }}</option>
          <option v-for="option in options.gridReference" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- The apps' hike time presets (Slow / Average / Fast / Trail Runner)
           rather than all nine raw paces, each labelled with its pace in the
           Measurement units chosen above. With nothing picked yet the server
           default (Average) shows, and nothing is sent unless one is chosen. -->
      <div v-if="hikeTimePresets.length" class="ob-field">
        <div class="ob-label-row">
          <label class="ob-label" for="settingsHikeTime">
            {{ $t('on_boarding.profile_settings_page.hike_time') }}
          </label>
          <a
            class="ob-info settings-info-link"
            data-info="hikeTime"
            :href="infoUrls.hikeTime"
            target="_blank"
            rel="noopener"
            :aria-label="$t('on_boarding.profile_settings_page.hike_time_info')"
            :title="$t('on_boarding.profile_settings_page.hike_time_info')"
          ><span class="ms" aria-hidden="true">info</span></a>
        </div>
        <p id="settingsHikeTimeHint" class="ob-hint">{{ $t('on_boarding.profile_settings_page.hike_time_hint') }}</p>
        <select id="settingsHikeTime" aria-describedby="settingsHikeTimeHint" v-model="shownHikeTime" class="ob-input">
          <option v-for="preset in hikeTimePresets" :key="preset.key" :value="preset.value" :data-preset="preset.key">
            {{ presetLabel(preset) }}
          </option>
        </select>
      </div>

      <div v-if="locales.length > 1" class="ob-field">
        <label class="ob-label" for="settingsLanguage">
          {{ $t('on_boarding.profile_settings_page.language') }}
        </label>
        <p id="settingsLanguageHint" class="ob-hint">{{ $t('on_boarding.profile_settings_page.language_hint') }}</p>
        <!-- Takes effect as soon as it's picked: the wizard swaps to the new
             language in place (onBoarding/switchLocale) rather than reloading,
             which would lose everything entered so far. -->
        <select
          id="settingsLanguage"
          aria-describedby="settingsLanguageHint"
          v-model="locale"
          class="ob-input"
          @change="switchLocale"
        >
          <option v-for="option in locales" :key="option.code" :value="option.code">
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>

    <template #footer>
      <button type="submit" class="ob-btn ob-btn-primary" :disabled="isLoading">
        <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>{{ $t('on_boarding.profile_settings_page.continue') }}</span>
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import { parseLocales, type SupportedLocale } from '../../../i18n/supportedLocales';
import {
  defaultSettingsForRegion,
  effectiveRegionCountry,
  hikeTimePresetPace,
  isAllowedPreference,
  SETTINGS_INFO_URLS,
  selectedHikeTimePresetKey,
  signupRegion,
  type PreferenceOption,
  type PreferenceOptions,
  type SignupCountry,
  type SignupHikeTimePreset,
} from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileSettingsPage',
  components: { OnBoardingFrame },
  data() {
    const state = this.$store.state.onBoarding;
    // Prefill from the region, mirroring the mobile apps' locale defaults —
    // except the region comes from the country the user picked two steps back
    // rather than a browser setting, which is better evidence.
    //
    // These are then saved like any other answer. That is deliberate and
    // differs from the account-settings page, which keeps a blank option so
    // editing an unrelated field can't silently record units the user never
    // saw: writing measurement_preference stamps a timestamp the phones read as
    // a real choice. Here the field is on screen and labelled when Finish is
    // pressed, so confirming it is a choice.
    const chosen = (state.countries || []).find(
      (entry: SignupCountry) => String(entry.id) === String(state.profileCountryId),
    ) || null;
    // A sub-region (a US state, a Canadian province) stores its own code, so
    // the region comes from its parent row.
    const country = effectiveRegionCountry(chosen, state.countries);
    const region = signupRegion(country, typeof navigator !== 'undefined' ? navigator.language : null);
    const regionDefaults = defaultSettingsForRegion(region);
    // Only preselect a default the server actually offers. A value missing from
    // the list leaves the <select> on selectedIndex -1, which renders as an
    // empty box — worse than showing "Use the default".
    const options: PreferenceOptions = state.preferenceOptions;
    const offered = (list: PreferenceOption[], value: string) => (
      isAllowedPreference(list, value) ? value : null
    );
    return {
      measurement: (state.profileMeasurement || offered(options.measurement, regionDefaults.measurementPreference)) as string | null,
      gridReference: (state.profileGridReference || offered(options.gridReference, regionDefaults.gridReference)) as string | null,
      hikeTime: state.profileHikeTime as string | null,
      // Defaults to the language the page is already being read in.
      locale: (state.profileLocale || (window as any).hiikerLocale || 'en') as string,
      locales: parseLocales((window as any).hiikerLocales) as SupportedLocale[],
      // The apps' info buttons: opened in a new tab so the wizard stays put.
      infoUrls: SETTINGS_INFO_URLS,
    };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    options(): PreferenceOptions {
      return this.$store.state.onBoarding.preferenceOptions;
    },
    // Tolerates an options object from before presets were published.
    hikeTimePresets(): SignupHikeTimePreset[] {
      return this.options.hikeTimePresets || [];
    },
    // What the dropdown shows: the saved pace, or the server default while
    // nothing is picked. Choosing an option is what records a pace, so an
    // untouched dropdown leaves hikeTime null and nothing is sent.
    shownHikeTime: {
      get(): string | null {
        const key = selectedHikeTimePresetKey(this.hikeTimePresets, this.hikeTime);
        const preset = this.hikeTimePresets.find((entry: SignupHikeTimePreset) => entry.key === key);
        return preset ? preset.value : this.hikeTime;
      },
      set(value: string | null) {
        this.hikeTime = value;
      },
    },
  },
  methods: {
    switchLocale() {
      this.$store.dispatch('onBoarding/switchLocale', this.locale);
    },
    // "Average — 5 km/h · 600 m/h", as the apps' hike time settings list it.
    presetLabel(preset: SignupHikeTimePreset): string {
      const pace = hikeTimePresetPace(preset, this.measurement);
      return pace ? `${preset.name} — ${pace}` : preset.name;
    },
    submit() {
      this.$store.commit('onBoarding/setProfileMeasurement', this.measurement);
      this.$store.commit('onBoarding/setProfileGridReference', this.gridReference);
      this.$store.commit('onBoarding/setProfileHikeTime', this.hikeTime);
      this.$store.commit('onBoarding/setProfileLocale', this.locale);
      this.$store.dispatch('onBoarding/submitProfileSettings');
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
